import { Injectable, Optional } from "@angular/core";
import { Cartesian2, Cartesian3, Cartographic, Ellipsoid, Math, SceneTransforms } from "cesium";
import { CesiumMapService } from "../..";

/* NOTE: ) is extracted from angular-cesium with updated types and removed unused methods which used geodesy */

@Injectable()
export class CoordinateConverter {
    constructor(@Optional() private cesiumService?: CesiumMapService) {}

    public static cartesian3ToLatLon(cartesian3: Cartesian3, ellipsoid?: Ellipsoid): { lon: number; lat: number; height: number } {
        const cart = Cartographic.fromCartesian(cartesian3, ellipsoid);

        return {
            lon: Math.toDegrees(cart.longitude),
            lat: Math.toDegrees(cart.latitude),
            height: cart.height,
        };
    }

    public screenToCartesian3(screenPos: { x: number; y: number }, addMapCanvasBoundsToPos?: boolean) {
        if (!this.cesiumService) {
            throw new Error("CesiumMapService should be provided in order to do screen position calculations");
        } else {
            const screenPosition: Cartesian2 = new Cartesian2(screenPos.x, screenPos.y);
            if (addMapCanvasBoundsToPos) {
                const mapBounds = this.cesiumService.getViewer().canvas.getBoundingClientRect();
                screenPosition.x += mapBounds.left;
                screenPosition.y += mapBounds.top;
            }

            const camera = this.cesiumService.getViewer().camera;

            return camera.pickEllipsoid(screenPosition);
        }
    }

    public cartesian3ToCartographic(cartesian: Cartesian3, ellipsoid?: Ellipsoid) {
        return Cartographic.fromCartesian(cartesian, ellipsoid);
    }

    public degreesToCartographic(longitude: number, latitude: number, height?: number) {
        return Cartographic.fromDegrees(longitude, latitude, height);
    }

    public middlePointByScreen(position0: Cartesian3, position1: Cartesian3): Cartesian3 {
        const scene = this.cesiumService?.getScene();

        if (!scene) {
            throw new Error("CesiumMapService should be provided in order to do screen position calculations");
        }
        const screenPosition1 = SceneTransforms.worldToDrawingBufferCoordinates(scene, position0);
        const screenPosition2 = SceneTransforms.worldToDrawingBufferCoordinates(scene, position1);
        if (!screenPosition1 || !screenPosition2) {
            throw new Error("Unable to project world positions to screen coordinates");
        }

        const middleScreenPoint = new Cartesian2(
            (screenPosition2.x + screenPosition1.x) / 2.0,
            (screenPosition2.y + screenPosition1.y) / 2.0
        );

        return scene.pickPosition(middleScreenPoint);
    }
}
